import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Avatar,
    Chip,
    Switch,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    Snackbar,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Fab,
    Tooltip,
    CircularProgress,
    LinearProgress,
    useTheme,
    useMediaQuery,
    TablePagination
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    Visibility,
    ExpandMore,
    PhotoCamera,
    Star,
    EmojiEvents,
    Security,
    Route,
    Person,
    Save,
    Cancel,
    CheckCircle,
    RadioButtonUnchecked
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api';

const SAFETY_RATINGS = ['Excellent', 'Good', 'Fair', 'Needs Improvement'];

function DriverSpotlightManagement() {
    const [spotlights, setSpotlights] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState('add'); // 'add', 'edit', 'view'
    const [currentSpotlight, setCurrentSpotlight] = useState(null);
    
    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    // Theme and responsive
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [formData, setFormData] = useState({
        driverId: '',
        title: 'Driver of the Month',
        description: '',
        profileImageUrl: '',
        spotlight: {
            bio: '',
            routeSpecialty: '',
            yearsOfExperience: '',
            milesThisYear: '',
            safetyRecord: {
                daysWithoutIncident: '',
                safetyRating: 'Good'
            },
            achievements: [],
            funFact: '',
            favoriteRoute: ''
        },
        qAndA: [],
        isActive: true,
        startDate: new Date(),
        endDate: null
    });
    const [newAchievement, setNewAchievement] = useState({ title: '', date: new Date(), description: '' });
    const [newQA, setNewQA] = useState({ question: '', answer: '' });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [errors, setErrors] = useState({});
    const { user } = useAuth();
    // Delete confirmation state
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [togglingId, setTogglingId] = useState(null);

    // Fetch spotlights and users
    useEffect(() => {
        fetchSpotlights();
        fetchUsers();
    }, []);

    const fetchSpotlights = async () => {
        setLoading(true);
        try {
            const timestamp = new Date().getTime();
            const response = await apiClient.get(`/team-engagement/spotlight?_t=${timestamp}`, {
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });
            const fetchedSpotlights = response.data.data || [];
            setSpotlights(fetchedSpotlights);
            setError(null);
        } catch (error) {
            console.error('Error fetching spotlights:', error);
            setError('Failed to fetch driver spotlights. Please try again later.');
            setSnackbar({
                open: true,
                message: 'Failed to fetch driver spotlights',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const endpoint = user.role === 'admin' ? '/admin/users' : '/users/company';
            const response = await apiClient.get(endpoint);
            const userData = user.role === 'admin' ? response.data.data?.users || [] : response.data.users || [];
            setUsers(userData);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const handleOpenDialog = (type, spotlight = null) => {
        setDialogType(type);
        setCurrentSpotlight(spotlight);
        
        if (type === 'add') {
            setFormData({
                driverId: '',
                title: 'Driver of the Month',
                description: '',
                profileImageUrl: '',
                spotlight: {
                    bio: '',
                    routeSpecialty: '',
                    yearsOfExperience: '',
                    milesThisYear: '',
                    safetyRecord: {
                        daysWithoutIncident: '',
                        safetyRating: 'Good'
                    },
                    achievements: [],
                    funFact: '',
                    favoriteRoute: ''
                },
                qAndA: [],
                isActive: true,
                startDate: new Date(),
                endDate: null
            });
        } else if (type === 'edit' && spotlight) {
            setFormData({
                driverId: spotlight.driverId._id || spotlight.driverId,
                title: spotlight.title || 'Driver of the Month',
                description: spotlight.description || '',
                profileImageUrl: spotlight.profileImageUrl || '',
                spotlight: {
                    bio: spotlight.spotlight?.bio || '',
                    routeSpecialty: spotlight.spotlight?.routeSpecialty || '',
                    yearsOfExperience: spotlight.spotlight?.yearsOfExperience || '',
                    milesThisYear: spotlight.spotlight?.milesThisYear || '',
                    safetyRecord: {
                        daysWithoutIncident: spotlight.spotlight?.safetyRecord?.daysWithoutIncident || '',
                        safetyRating: spotlight.spotlight?.safetyRecord?.safetyRating || 'Good'
                    },
                    achievements: spotlight.spotlight?.achievements || [],
                    funFact: spotlight.spotlight?.funFact || '',
                    favoriteRoute: spotlight.spotlight?.favoriteRoute || ''
                },
                qAndA: spotlight.qAndA || [],
                isActive: spotlight.isActive || false,
                startDate: spotlight.startDate ? new Date(spotlight.startDate) : new Date(),
                endDate: spotlight.endDate ? new Date(spotlight.endDate) : null
            });
        }
        
        setErrors({});
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setCurrentSpotlight(null);
        setErrors({});
    };

    const handleInputChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child, grandchild] = field.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: grandchild ? {
                        ...prev[parent][child],
                        [grandchild]: value
                    } : value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));
        }
        
        // Clear specific error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const addAchievement = () => {
        if (newAchievement.title && newAchievement.description) {
            setFormData(prev => ({
                ...prev,
                spotlight: {
                    ...prev.spotlight,
                    achievements: [...prev.spotlight.achievements, { ...newAchievement }]
                }
            }));
            setNewAchievement({ title: '', date: new Date(), description: '' });
        }
    };

    const removeAchievement = (index) => {
        setFormData(prev => ({
            ...prev,
            spotlight: {
                ...prev.spotlight,
                achievements: prev.spotlight.achievements.filter((_, i) => i !== index)
            }
        }));
    };

    const addQA = () => {
        if (newQA.question && newQA.answer) {
            setFormData(prev => ({
                ...prev,
                qAndA: [...prev.qAndA, { ...newQA }]
            }));
            setNewQA({ question: '', answer: '' });
        }
    };

    const removeQA = (index) => {
        setFormData(prev => ({
            ...prev,
            qAndA: prev.qAndA.filter((_, i) => i !== index)
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.driverId) newErrors.driverId = 'Driver is required';
        if (!formData.title.trim()) newErrors.title = 'Title is required';
        if (!formData.description.trim()) newErrors.description = 'Description is required';
        if (!formData.spotlight.bio.trim()) newErrors['spotlight.bio'] = 'Bio is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            const endpoint = dialogType === 'add' 
                ? '/team-engagement/spotlight'
                : `/team-engagement/spotlight/${currentSpotlight._id}`;
            
            const method = dialogType === 'add' ? 'post' : 'put';
            
            const response = await apiClient[method](endpoint, formData);
            
            setSnackbar({
                open: true,
                message: `Driver spotlight ${dialogType === 'add' ? 'created' : 'updated'} successfully`,
                severity: 'success'
            });
            
            handleCloseDialog();
            fetchSpotlights();
        } catch (error) {
            console.error('Error saving spotlight:', error);
            setSnackbar({
                open: true,
                message: `Failed to ${dialogType} driver spotlight: ${error.response?.data?.message || error.message}`,
                severity: 'error'
            });
        }
    };

    const handleToggleActive = async (spotlight) => {
        try {
            setTogglingId(spotlight._id);
            await apiClient.put(`/team-engagement/spotlight/${spotlight._id}`, {
                isActive: !spotlight.isActive
            });
            setSnackbar({
                open: true,
                message: `Spotlight ${!spotlight.isActive ? 'activated' : 'deactivated'} successfully`,
                severity: 'success'
            });
            fetchSpotlights();
        } catch (error) {
            console.error('Error toggling spotlight:', error);
            setSnackbar({
                open: true,
                message: 'Failed to update spotlight status',
                severity: 'error'
            });
        } finally {
            setTogglingId(null);
        }
    };

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const selectedDriver = users.find(u => u._id === formData.driverId);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ px: { xs: 2, md: 3 } }}>
            {/* Header aligned to match User Management spacing */}
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                        Driver Spotlight Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage and showcase outstanding drivers in your organization
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog('add')}
                    size={isMobile ? "small" : "medium"}
                    sx={{ 
                        borderRadius: 2,
                        px: 3,
                        fontWeight: 600,
                        minWidth: { xs: '100%', sm: 'auto' }
                    }}
                >
                    Create New Spotlight
                </Button>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Summary Card */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Driver Spotlights ({spotlights.length})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Active: {spotlights.filter(s => s.isActive).length} • 
                    Inactive: {spotlights.filter(s => !s.isActive).length}
                </Typography>
            </Paper>

            {/* Enhanced Table */}
            <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                <TableCell sx={{ fontWeight: 600 }}>Driver</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {spotlights.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                                        <Stack alignItems="center" spacing={2}>
                                            <Avatar sx={{ bgcolor: 'grey.100', width: 64, height: 64 }}>
                                                <EmojiEvents sx={{ fontSize: 32, color: 'grey.400' }} />
                                            </Avatar>
                                            <Box textAlign="center">
                                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                                    No driver spotlights found
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Create your first driver spotlight to recognize outstanding performance
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                spotlights
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((spotlight) => (
                                        <TableRow 
                                            key={spotlight._id} 
                                            hover
                                            sx={{ 
                                                '&:hover': { bgcolor: 'action.hover' },
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => handleOpenDialog('view', spotlight)}
                                        >
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" spacing={2}>
                                                    <Avatar
                                                        src={spotlight.profileImageUrl}
                                                        sx={{ 
                                                            width: 40, 
                                                            height: 40,
                                                            bgcolor: spotlight.isActive ? 'primary.main' : 'grey.400'
                                                        }}
                                                    >
                                                        {spotlight.driverId?.name?.charAt(0) || 'D'}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight={500}>
                                                            {spotlight.driverId?.name || 'Unknown Driver'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {spotlight.driverId?.email || 'No email'}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {spotlight.title}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {spotlight.description && spotlight.description.length > 50 
                                                            ? `${spotlight.description.substring(0, 50)}...`
                                                            : spotlight.description || 'No description'
                                                        }
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell 
                                                onClick={(e) => e.stopPropagation()}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onKeyDown={(e) => e.stopPropagation()}
                                            >
                                                <FormControlLabel
                                                    onClick={(e) => e.stopPropagation()}
                                                    control={
                                                        <Switch
                                                            size="small"
                                                            checked={!!spotlight.isActive}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                handleToggleActive(spotlight);
                                                            }}
                                                            disabled={togglingId === spotlight._id}
                                                        />
                                                    }
                                                    label={spotlight.isActive ? 'Active' : 'Inactive'}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {new Date(spotlight.startDate).toLocaleDateString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {spotlight.createdBy?.name || 'Unknown'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    <Tooltip title="View Details">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenDialog('view', spotlight);
                                                            }}
                                                        >
                                                            <Visibility />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenDialog('edit', spotlight);
                                                            }}
                                                        >
                                                            <Edit />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {/* Activation toggle moved to Status column */}
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setDeleteTarget(spotlight);
                                                                setConfirmDeleteOpen(true);
                                                            }}
                                                            color="error"
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {spotlights.length > 0 && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={spotlights.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            borderTop: 1,
                            borderColor: 'divider',
                            bgcolor: 'grey.50'
                        }}
                    />
                )}
            </Paper>

            {/* Create/Edit Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{ 
                    sx: { 
                        borderRadius: 3,
                        maxHeight: '90vh'
                    } 
                }}
            >
                <DialogTitle sx={{ 
                    pb: 2, 
                    borderBottom: 1, 
                    borderColor: 'divider',
                    bgcolor: 'grey.50'
                }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{ 
                            bgcolor: dialogType === 'add' ? 'primary.main' : 
                                     dialogType === 'edit' ? 'warning.main' : 'info.main',
                            width: 40,
                            height: 40
                        }}>
                            {dialogType === 'add' ? <Add /> : dialogType === 'edit' ? <Edit /> : <Visibility />}
                        </Avatar>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {dialogType === 'add' ? 'Create New' : dialogType === 'edit' ? 'Edit' : 'View'} Driver Spotlight
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {dialogType === 'add' ? 'Recognize outstanding driver performance' : 
                                 dialogType === 'edit' ? 'Update spotlight information' : 
                                 'Review spotlight details'}
                            </Typography>
                        </Box>
                    </Stack>
                </DialogTitle>
                
                <DialogContent sx={{ pt: 3, pb: 0 }}>
                    {/* Required Fields Notice */}
                    {dialogType !== 'view' && (
                        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                            <Typography variant="body2">
                                <strong>Required fields are marked with an asterisk (*)</strong> - Driver, Title, Description, and Bio are required to create a spotlight.
                            </Typography>
                        </Alert>
                    )}

                    {/* Validation Errors Alert */}
                    {Object.keys(errors).length > 0 && (
                        <Alert 
                            severity="error" 
                            sx={{ mb: 3, borderRadius: 2 }}
                            onClose={() => setErrors({})}
                        >
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                Please fix the following errors:
                            </Typography>
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {Object.entries(errors).map(([field, message]) => (
                                    <li key={field}>
                                        <Typography variant="body2">
                                            {message}
                                        </Typography>
                                    </li>
                                ))}
                            </ul>
                        </Alert>
                    )}

                    <Grid container spacing={4}>
                        {/* Basic Information */}
                        <Grid item xs={12}>
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardHeader 
                                    title="Basic Information" 
                                    sx={{ 
                                        bgcolor: 'primary.50',
                                        borderBottom: 1,
                                        borderColor: 'divider'
                                    }}
                                    titleTypographyProps={{ 
                                        variant: 'h6', 
                                        fontWeight: 600 
                                    }}
                                />
                                <CardContent sx={{ pt: 3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth error={!!errors.driverId}>
                                                <InputLabel>Select Driver *</InputLabel>
                                                <Select
                                                    value={formData.driverId}
                                                    onChange={(e) => handleInputChange('driverId', e.target.value)}
                                                    label="Select Driver *"
                                                    disabled={dialogType === 'view'}
                                                >
                                                    {users.map((user) => (
                                                        <MenuItem key={user._id} value={user._id}>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <Avatar sx={{ width: 24, height: 24 }}>
                                                                    {user.name?.charAt(0)}
                                                                </Avatar>
                                                                <span>{user.name}</span>
                                                            </Stack>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {errors.driverId && (
                                                    <Typography variant="caption" color="error">
                                                        {errors.driverId}
                                                    </Typography>
                                                )}
                                            </FormControl>
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Title *"
                                                value={formData.title}
                                                onChange={(e) => handleInputChange('title', e.target.value)}
                                                error={!!errors.title}
                                                helperText={errors.title}
                                                disabled={dialogType === 'view'}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Description *"
                                                value={formData.description}
                                                onChange={(e) => handleInputChange('description', e.target.value)}
                                                error={!!errors.description}
                                                helperText={errors.description}
                                                multiline
                                                rows={2}
                                                disabled={dialogType === 'view'}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Profile Image URL"
                                                value={formData.profileImageUrl}
                                                onChange={(e) => handleInputChange('profileImageUrl', e.target.value)}
                                                disabled={dialogType === 'view'}
                                                placeholder="https://example.com/driver-photo.jpg"
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <DatePicker
                                                label="Start Date"
                                                value={formData.startDate}
                                                onChange={(date) => handleInputChange('startDate', date)}
                                                disabled={dialogType === 'view'}
                                                slotProps={{
                                                    textField: { fullWidth: true }
                                                }}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={formData.isActive}
                                                        onChange={(e) => handleInputChange('isActive', e.target.checked)}
                                                        disabled={dialogType === 'view'}
                                                    />
                                                }
                                                label="Active Spotlight"
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Driver Profile */}
                        <Grid item xs={12}>
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardHeader 
                                    title="Driver Profile" 
                                    sx={{ 
                                        bgcolor: 'success.50',
                                        borderBottom: 1,
                                        borderColor: 'divider'
                                    }}
                                    titleTypographyProps={{ 
                                        variant: 'h6', 
                                        fontWeight: 600 
                                    }}
                                />
                                <CardContent sx={{ pt: 3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Bio *"
                                                value={formData.spotlight.bio}
                                                onChange={(e) => handleInputChange('spotlight.bio', e.target.value)}
                                                error={!!errors['spotlight.bio']}
                                                helperText={errors['spotlight.bio'] || "Tell us about this driver's background, experience, and what makes them special..."}
                                                multiline
                                                rows={3}
                                                disabled={dialogType === 'view'}
                                                placeholder="Tell us about this driver's background, experience, and what makes them special..."
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Route Specialty"
                                                value={formData.spotlight.routeSpecialty}
                                                onChange={(e) => handleInputChange('spotlight.routeSpecialty', e.target.value)}
                                                disabled={dialogType === 'view'}
                                                placeholder="Long haul, Local delivery, etc."
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Years of Experience"
                                                type="number"
                                                value={formData.spotlight.yearsOfExperience}
                                                onChange={(e) => handleInputChange('spotlight.yearsOfExperience', e.target.value)}
                                                disabled={dialogType === 'view'}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Miles This Year"
                                                type="number"
                                                value={formData.spotlight.milesThisYear}
                                                onChange={(e) => handleInputChange('spotlight.milesThisYear', e.target.value)}
                                                disabled={dialogType === 'view'}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Days Without Incident"
                                                type="number"
                                                value={formData.spotlight.safetyRecord.daysWithoutIncident}
                                                onChange={(e) => handleInputChange('spotlight.safetyRecord.daysWithoutIncident', e.target.value)}
                                                disabled={dialogType === 'view'}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Safety Rating</InputLabel>
                                                <Select
                                                    value={formData.spotlight.safetyRecord.safetyRating}
                                                    onChange={(e) => handleInputChange('spotlight.safetyRecord.safetyRating', e.target.value)}
                                                    label="Safety Rating"
                                                    disabled={dialogType === 'view'}
                                                >
                                                    {SAFETY_RATINGS.map((rating) => (
                                                        <MenuItem key={rating} value={rating}>
                                                            {rating}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Fun Fact"
                                                value={formData.spotlight.funFact}
                                                onChange={(e) => handleInputChange('spotlight.funFact', e.target.value)}
                                                disabled={dialogType === 'view'}
                                                placeholder="Something interesting about this driver..."
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Favorite Route"
                                                value={formData.spotlight.favoriteRoute}
                                                onChange={(e) => handleInputChange('spotlight.favoriteRoute', e.target.value)}
                                                disabled={dialogType === 'view'}
                                                placeholder="I-80 cross country, Local metro area, etc."
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Achievements */}
                        <Grid item xs={12}>
                            <Accordion sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
                                <AccordionSummary 
                                    expandIcon={<ExpandMore />}
                                    sx={{
                                        bgcolor: 'warning.50',
                                        borderRadius: '8px 8px 0 0',
                                        '&.Mui-expanded': {
                                            borderRadius: '8px 8px 0 0'
                                        }
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                                            <EmojiEvents sx={{ fontSize: 20 }} />
                                        </Avatar>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Achievements
                                        </Typography>
                                        <Chip 
                                            label={formData.spotlight.achievements.length} 
                                            size="small"
                                            color="warning"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </Stack>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 2 }}>
                                    {dialogType !== 'view' && (
                                        <Card variant="outlined" sx={{ mb: 2 }}>
                                            <CardContent>
                                                <Typography variant="subtitle2" gutterBottom>Add New Achievement</Typography>
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={12} md={4}>
                                                        <TextField
                                                            fullWidth
                                                            label="Title"
                                                            value={newAchievement.title}
                                                            onChange={(e) => setNewAchievement(prev => ({ ...prev, title: e.target.value }))}
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={3}>
                                                        <DatePicker
                                                            label="Date"
                                                            value={newAchievement.date}
                                                            onChange={(date) => setNewAchievement(prev => ({ ...prev, date }))}
                                                            slotProps={{
                                                                textField: { size: 'small', fullWidth: true }
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <TextField
                                                            fullWidth
                                                            label="Description"
                                                            value={newAchievement.description}
                                                            onChange={(e) => setNewAchievement(prev => ({ ...prev, description: e.target.value }))}
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={1}>
                                                        <Button
                                                            variant="contained"
                                                            onClick={addAchievement}
                                                            size="small"
                                                            disabled={!newAchievement.title || !newAchievement.description}
                                                        >
                                                            Add
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    )}
                                    
                                    <Stack spacing={1}>
                                        {formData.spotlight.achievements.map((achievement, index) => (
                                            <Card key={index} variant="outlined">
                                                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Box>
                                                            <Typography variant="subtitle2" fontWeight="bold">
                                                                {achievement.title}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {achievement.description}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {new Date(achievement.date).toLocaleDateString()}
                                                            </Typography>
                                                        </Box>
                                                        {dialogType !== 'view' && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => removeAchievement(index)}
                                                                color="error"
                                                            >
                                                                <Delete />
                                                            </IconButton>
                                                        )}
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>

                        {/* Q&A Section */}
                        <Grid item xs={12}>
                            <Accordion sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
                                <AccordionSummary 
                                    expandIcon={<ExpandMore />}
                                    sx={{
                                        bgcolor: 'info.50',
                                        borderRadius: '8px 8px 0 0',
                                        '&.Mui-expanded': {
                                            borderRadius: '8px 8px 0 0'
                                        }
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Avatar sx={{ bgcolor: 'info.main', width: 32, height: 32 }}>
                                            <Person sx={{ fontSize: 20 }} />
                                        </Avatar>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Q&A
                                        </Typography>
                                        <Chip 
                                            label={formData.qAndA.length} 
                                            size="small"
                                            color="info"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </Stack>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 2 }}>
                                    {dialogType !== 'view' && (
                                        <Card variant="outlined" sx={{ mb: 2 }}>
                                            <CardContent>
                                                <Typography variant="subtitle2" gutterBottom>Add New Q&A</Typography>
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={12} md={5}>
                                                        <TextField
                                                            fullWidth
                                                            label="Question"
                                                            value={newQA.question}
                                                            onChange={(e) => setNewQA(prev => ({ ...prev, question: e.target.value }))}
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <TextField
                                                            fullWidth
                                                            label="Answer"
                                                            value={newQA.answer}
                                                            onChange={(e) => setNewQA(prev => ({ ...prev, answer: e.target.value }))}
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={1}>
                                                        <Button
                                                            variant="contained"
                                                            onClick={addQA}
                                                            size="small"
                                                            disabled={!newQA.question || !newQA.answer}
                                                        >
                                                            Add
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    )}
                                    
                                    <Stack spacing={1}>
                                        {formData.qAndA.map((qa, index) => (
                                            <Card key={index} variant="outlined">
                                                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="start">
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                                                Q: {qa.question}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                A: {qa.answer}
                                                            </Typography>
                                                        </Box>
                                                        {dialogType !== 'view' && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => removeQA(index)}
                                                                color="error"
                                                            >
                                                                <Delete />
                                                            </IconButton>
                                                        )}
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ 
                    px: 3, 
                    py: 3,
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                    gap: 2
                }}>
                    <Button
                        onClick={handleCloseDialog}
                        startIcon={<Cancel />}
                        size="large"
                        sx={{ 
                            borderRadius: 2,
                            px: 3,
                            fontWeight: 600
                        }}
                    >
                        {dialogType === 'view' ? 'Close' : 'Cancel'}
                    </Button>
                    {dialogType !== 'view' && (
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            startIcon={<Save />}
                            size="large"
                            sx={{ 
                                borderRadius: 2,
                                px: 4,
                                py: 1.5,
                                fontWeight: 600,
                                boxShadow: 2,
                                '&:hover': {
                                    boxShadow: 4
                                }
                            }}
                        >
                            {dialogType === 'add' ? 'Create Spotlight' : 'Update Spotlight'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
            {/* Delete Confirmation Dialog */}
            <Dialog
                open={confirmDeleteOpen}
                onClose={() => setConfirmDeleteOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete
                        {` "${deleteTarget?.title || 'Driver Spotlight'}"`}? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
                    <Button
                        onClick={async () => {
                            if (!deleteTarget) return;
                            try {
                                await apiClient.delete(`/team-engagement/spotlight/${deleteTarget._id}`);
                                setSnackbar({ open: true, message: 'Driver spotlight deleted', severity: 'success' });
                                setConfirmDeleteOpen(false);
                                setDeleteTarget(null);
                                fetchSpotlights();
                            } catch (error) {
                                console.error('Error deleting spotlight:', error);
                                setSnackbar({ open: true, message: 'Failed to delete driver spotlight', severity: 'error' });
                            }
                        }}
                        color="error"
                        variant="contained"
                        startIcon={<Delete />}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}

export default DriverSpotlightManagement; 